import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const linkedinMessageArticle: BlogArticle = {
  key: BlogArticleKey.LINKEDIN_MESSAGE,
  slug: BlogSlugs.LINKEDIN_MESSAGE,
  categoryId: "04",
  date: "2026-07-27",
  readTime: 7,
  locales: {
    fr: {
      title: "Message de prospection LinkedIn qui obtient des réponses",
      excerpt:
        "Tes messages restent sans réponse parce qu'ils ressemblent à tous les autres. Voici comment en écrire un qui prouve en deux lignes que tu as ciblé cette personne.",
      description:
        "Les templates ne marchent plus. Comment écrire un message de prospection LinkedIn qui obtient des réponses, à partir des vrais mots de tes clients.",
      ctaTitle: "Écris des messages qui sonnent vrai",
      ctaText:
        "On part des mots que tes clients emploient déjà pour en tirer des accroches et des relances qui ne ressemblent pas à un template. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Chaque jour, ton prospect reçoit des dizaines de messages qui commencent par *j'espère que vous allez bien* et enchaînent sur *je me permets de vous contacter car nous aidons les entreprises comme la vôtre à*. Il les supprime sans les lire. Le tien y passe aussi.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est pas que la prospection LinkedIn ne marche plus. C'est que la prospection au template, elle, ne marche plus. Et la bonne nouvelle, c'est que ça n'a jamais été aussi facile de se démarquer : il suffit d'écrire un message qui parle vraiment de la personne en face.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi tes messages restent sans réponse" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La plupart des messages de prospection ont le même défaut de fond : ils parlent du vendeur, pas du prospect. Ils décrivent une offre, une solution, une entreprise. Or le prospect ne se réveille pas le matin en pensant à ton offre. Il pense à ses problèmes à lui.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Ils sont **génériques** : le même texte envoyé à 500 personnes, avec juste le prénom qui change.",
            "Ils parlent **de toi** : nous faisons, notre solution, je propose.",
            "Ils **supposent** le besoin au lieu de partir d'un signe réel que la personne l'a.",
            "Ils sont **trop longs** : un pavé qu'on n'a pas le temps de lire.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un bon message de prospection ne cherche pas à vendre. Il cherche à prouver, en deux lignes, que tu as contacté cette personne pour une raison précise.",
        },
        { type: ArticleBlockType.H2, text: "Le même message, avant et après" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le message ignoré",
            items: [
              "*Bonjour Marc, j'espère que vous allez bien. Je me permets de vous contacter car nous aidons les directeurs commerciaux à améliorer leur performance grâce à notre plateforme tout-en-un. Seriez-vous disponible pour un échange de 15 minutes cette semaine ?*",
            ],
          },
          good: {
            title: "Le message qui répond",
            items: [
              "*Bonjour Marc, j'ai vu que vous veniez de recruter trois commerciaux d'un coup. Faire monter une équipe aussi vite, c'est souvent là que les process craquent. Comment vous gérez l'onboarding en ce moment ?*",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le second ne vend rien. Il montre qu'il a ciblé Marc pour une raison, le recrutement, touche un problème réel, faire monter une équipe vite, et ouvre une conversation au lieu de demander un rendez-vous. C'est ça qui obtient une réponse.",
        },
        { type: ArticleBlockType.H2, text: "Les 4 ingrédients d'un message qui répond" },
        { type: ArticleBlockType.H3, text: "1. Une accroche ancrée dans sa situation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Commence par quelque chose qui le concerne lui, pas toi : un événement, un signal, une phrase qu'il a publiée. Un fait précis sur son actualité prouve immédiatement que ce n'est pas un envoi de masse.",
        },
        { type: ArticleBlockType.H3, text: "2. Un problème réel, formulé avec ses mots" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Touche une douleur que cette situation crée, dans les mots que ta cible emploie vraiment. Pas *optimiser votre performance commerciale* mais *faire monter une équipe qui vient de doubler*. Plus c'est concret et reconnaissable, plus ça résonne.",
        },
        { type: ArticleBlockType.H3, text: "3. Ce que tu apportes, en une ligne maximum" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ne déballe pas ton offre. Une phrase suffit pour situer ce que tu fais, et encore, souvent elle peut attendre le message suivant. L'objectif du premier message, c'est d'ouvrir, pas de conclure.",
        },
        { type: ArticleBlockType.H3, text: "4. Une question simple qui ouvre la conversation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Termine par une question à laquelle il peut répondre en une phrase. *Comment vous gérez ça en ce moment ?* marche mieux que *seriez-vous disponible pour un rendez-vous ?*. Tu demandes un avis, pas un créneau.",
        },
        { type: ArticleBlockType.H2, text: "La séquence : relancer sans harceler" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un seul message obtient rarement une réponse, même le meilleur. La relance fait partie du jeu. Mais relancer ne veut pas dire répéter : chaque message d'une séquence doit apporter quelque chose de nouveau.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Message 1** : l'accroche sur sa situation, une question ouverte.",
            "**Relance 1**, trois à quatre jours après : un angle différent, une ressource utile, un exemple concret. Jamais *je me permets de revenir vers vous*.",
            "**Relance 2**, une semaine après : une porte de sortie élégante, qui laisse la conversation ouverte sans insister.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Deux ou trois messages utiles et espacés suffisent. Au-delà, tu bascules du suivi au harcèlement, et tu abîmes ton image pour rien.",
        },
        { type: ArticleBlockType.H2, text: "Le vrai secret : où trouver les bons mots" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tout ce qui précède repose sur une chose : connaître la situation et les mots de ta cible. Et le meilleur endroit pour les trouver, ce ne sont pas des templates trouvés en ligne. Ce sont tes propres conversations clients.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Quand tu écoutes tes appels, tu entends comment tes clients décrivent leurs problèmes, quelles situations les ont poussés à chercher une solution, quels mots exacts ils emploient. Ce sont ces formulations, réutilisées dans tes messages, qui font mouche, parce qu'elles sonnent vrai.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On part de tes appels clients, on en extrait les situations, les douleurs et les mots exacts de tes prospects, puis on réutilise cette matière pour préparer tes messages et tes séquences. Tu n'écris plus à partir d'un template, mais à partir de ce que disent vraiment tes clients. Une donnée captée une fois, réutilisée pour parler juste.",
          linkLabel: "Découvrir Messages et séquences",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un message de prospection qui obtient des réponses ne parle pas de toi, il parle de la personne en face. Ancre-le dans sa situation, touche un problème réel avec ses mots, reste court, et ouvre une conversation au lieu de demander un rendez-vous. Relance deux ou trois fois en apportant à chaque fois quelque chose de neuf. Et surtout, puise tes mots dans ce que disent vraiment tes clients, pas dans un template que tout le monde utilise déjà.",
        },
      ],
      faq: [
        {
          question: "Pourquoi mes messages de prospection restent sans réponse ?",
          answer:
            "Le plus souvent parce qu'ils parlent de toi et de ton offre, avec un template envoyé à tout le monde. Un message obtient une réponse quand il parle de la situation réelle du prospect et emploie ses mots, pas les tiens.",
        },
        {
          question: "Faut-il personnaliser chaque message de prospection ?",
          answer:
            "Oui, mais pas au sens cosmétique du prénom qui change. La vraie personnalisation, c'est ancrer le message dans la situation du prospect : un événement, un besoin, une phrase qu'il a lui-même employée. C'est ce qui prouve que tu l'as ciblé pour une raison.",
        },
        {
          question: "Quelle est la bonne longueur pour un message de prospection LinkedIn ?",
          answer:
            "Court. Trois à cinq phrases suffisent : une accroche ancrée dans sa situation, ce que tu apportes en une ligne, et une question simple. Un pavé se fait ignorer, même s'il est bien écrit.",
        },
        {
          question: "Combien de relances envoyer en prospection ?",
          answer:
            "Deux à trois relances espacées et utiles suffisent. Chaque relance doit apporter quelque chose de nouveau, un angle ou une ressource, pas juste répéter le premier message. Au-delà, tu bascules du suivi au harcèlement.",
        },
      ],
    },
    en: {
      title: "LinkedIn prospecting message: the method that gets replies",
      excerpt:
        "Your messages go unanswered because they look like every other one. Here is how to write one that proves in two lines that you picked this person on purpose.",
      description:
        "Templates no longer work. How to write a LinkedIn prospecting message that gets replies, built from the real words of your customers.",
      ctaTitle: "Write messages that ring true",
      ctaText:
        "We start from the words your customers already use and turn them into openers and follow-ups that read nothing like a template. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Every day your prospect gets dozens of messages opening on *I hope this finds you well* and carrying on with *I am reaching out because we help companies like yours*. They delete them unread. Yours goes the same way.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is not that LinkedIn prospecting stopped working. It is that template prospecting stopped working. And the good news is that standing out has never been easier: you only have to write a message that is genuinely about the person on the other side.",
        },
        { type: ArticleBlockType.H2, text: "Why your messages go unanswered" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most prospecting messages share the same underlying flaw: they talk about the seller, not the prospect. They describe an offer, a solution, a company. But your prospect does not wake up thinking about your offer. They think about their own problems.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "They are **generic**: the same text sent to 500 people, with only the first name changing.",
            "They talk **about you**: we do, our solution, I am offering.",
            "They **assume** the need instead of starting from a real sign that the person has it.",
            "They are **too long**: a wall of text nobody has time to read.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A good prospecting message is not trying to sell. It is trying to prove, in two lines, that you contacted this person for a precise reason.",
        },
        { type: ArticleBlockType.H2, text: "The same message, before and after" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The ignored message",
            items: [
              "*Hi Marc, I hope this finds you well. I am reaching out because we help sales directors improve their performance with our all-in-one platform. Would you have 15 minutes for a chat this week?*",
            ],
          },
          good: {
            title: "The message that gets a reply",
            items: [
              "*Hi Marc, I saw you just hired three sales reps at once. Ramping a team up that fast is usually where the process cracks. How are you handling onboarding at the moment?*",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The second one sells nothing. It shows Marc was picked for a reason, the hiring, touches a real problem, ramping a team up fast, and opens a conversation instead of asking for a meeting. That is what gets a reply.",
        },
        { type: ArticleBlockType.H2, text: "The 4 ingredients of a message that gets answers" },
        { type: ArticleBlockType.H3, text: "1. An opener anchored in their situation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Start with something about them, not about you: an event, a signal, something they published. A precise fact about their own news immediately proves this is not a mass send.",
        },
        { type: ArticleBlockType.H3, text: "2. A real problem, in their own words" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Touch a pain that situation creates, in the words your target actually uses. Not *optimise your sales performance* but *ramp up a team that just doubled*. The more concrete and recognisable it is, the more it lands.",
        },
        { type: ArticleBlockType.H3, text: "3. What you bring, in one line at most" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Do not unpack your offer. One sentence is enough to place what you do, and it can often wait for the next message. The first message exists to open, not to close.",
        },
        { type: ArticleBlockType.H3, text: "4. A simple question that opens the conversation" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "End on a question they can answer in one sentence. *How are you handling that right now?* works better than *would you be available for a meeting?*. You are asking for a view, not for a slot.",
        },
        { type: ArticleBlockType.H2, text: "The sequence: following up without pestering" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A single message rarely gets a reply, even the best one. Following up is part of the game. But following up does not mean repeating: every message in a sequence has to bring something new.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Message 1**: the opener on their situation, an open question.",
            "**Follow-up 1**, three to four days later: a different angle, a useful resource, a concrete example. Never *just circling back on this*.",
            "**Follow-up 2**, a week later: a graceful way out that leaves the conversation open without pushing.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Two or three useful, spaced-out messages are enough. Beyond that you move from following up to pestering, and you damage your reputation for nothing.",
        },
        { type: ArticleBlockType.H2, text: "The real secret: where to find the right words" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Everything above rests on one thing: knowing your target's situation and words. And the best place to find them is not a template you found online. It is your own customer conversations.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Listening back to your calls, you hear how your customers describe their problems, which situations pushed them to look for a solution, which exact words they use. Those formulations, reused in your messages, are what lands, because they ring true.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We start from your customer calls, pull out the situations, the pains and the exact words of your prospects, then reuse that material to prepare your messages and your sequences. You no longer write from a template, but from what your customers actually say. Captured once, reused to say the right thing.",
          linkLabel: "Discover Messages and sequences",
          linkPath: ProtectedPaths.MESSAGES_CONTENT,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A prospecting message that gets replies is not about you, it is about the person on the other side. Anchor it in their situation, touch a real problem in their words, keep it short, and open a conversation instead of asking for a meeting. Follow up two or three times, bringing something new each time. And above all, take your words from what your customers actually say, not from a template everyone else is already using.",
        },
      ],
      faq: [
        {
          question: "Why do my prospecting messages go unanswered?",
          answer:
            "Usually because they talk about you and your offer, from a template sent to everyone. A message gets a reply when it speaks to the prospect's real situation and uses their words, not yours.",
        },
        {
          question: "Do you have to personalise every prospecting message?",
          answer:
            "Yes, but not in the cosmetic sense of swapping the first name. Real personalisation means anchoring the message in the prospect's situation: an event, a need, a sentence they used themselves. That is what proves you picked them for a reason.",
        },
        {
          question: "What is the right length for a LinkedIn prospecting message?",
          answer:
            "Short. Three to five sentences are enough: an opener anchored in their situation, what you bring in one line, and a simple question. A wall of text gets ignored, however well written.",
        },
        {
          question: "How many follow-ups should you send when prospecting?",
          answer:
            "Two to three spaced-out, useful follow-ups are enough. Each one has to bring something new, an angle or a resource, not just repeat the first message. Beyond that you move from following up to pestering.",
        },
      ],
    },
  },
};
